import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { ProductDetail } from '@/mocks/productDetails';

// Map Supabase row → ProductDetail
function rowToProduct(row: Record<string, unknown>): ProductDetail {
  return {
    id: Number(row.id),
    nameAr: String(row.name_ar ?? ''),
    category: String(row.category ?? 'PS5'),
    badge: String(row.badge ?? ''),
    rating: Number(row.rating ?? 5),
    reviews: Number(row.reviews ?? 0),
    price: Number(row.price ?? 0),
    originalPrice: Number(row.original_price ?? 0),
    stock: Number(row.stock ?? 0),
    shortDesc: String(row.short_desc ?? ''),
    longDesc: String(row.long_desc ?? ''),
    images: Array.isArray(row.images) ? (row.images as string[]) : [],
    highlights: Array.isArray(row.highlights) ? (row.highlights as string[]) : [],
    specs: Array.isArray(row.specs) ? (row.specs as { label: string; value: string }[]) : [],
    inBox: Array.isArray(row.in_box) ? (row.in_box as string[]) : [],
    relatedIds: Array.isArray(row.related_ids) ? (row.related_ids as number[]) : [],
  };
}

// Map ProductDetail → Supabase insert/update payload
function productToRow(p: Omit<ProductDetail, 'id'>) {
  return {
    name_ar: p.nameAr,
    category: p.category,
    badge: p.badge ?? '',
    rating: p.rating,
    reviews: p.reviews,
    price: p.price,
    original_price: p.originalPrice,
    stock: p.stock ?? 0,
    short_desc: p.shortDesc ?? '',
    long_desc: p.longDesc ?? '',
    images: p.images ?? [],
    highlights: p.highlights ?? [],
    specs: p.specs ?? [],
    in_box: p.inBox ?? [],
    related_ids: p.relatedIds ?? [],
    updated_at: new Date().toISOString(),
  };
}

export function useAdminProducts() {
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setProducts((data ?? []).map(rowToProduct));
    } catch (e) {
      setError('تعذّر تحميل المنتجات');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addProduct = useCallback(async (product: Omit<ProductDetail, 'id'>): Promise<boolean> => {
    try {
      const { data, error: err } = await supabase
        .from('products')
        .insert(productToRow(product))
        .select()
        .maybeSingle();
      if (err) throw err;
      if (data) {
        setProducts(prev => [rowToProduct(data as Record<string, unknown>), ...prev]);
      }
      return true;
    } catch (e) {
      console.error('addProduct error:', e);
      return false;
    }
  }, []);

  const updateProduct = useCallback(async (id: number, data: Partial<ProductDetail>): Promise<boolean> => {
    try {
      const payload: Partial<ReturnType<typeof productToRow>> & { updated_at: string } = {
        updated_at: new Date().toISOString(),
      };
      if (data.nameAr !== undefined) payload.name_ar = data.nameAr;
      if (data.category !== undefined) payload.category = data.category;
      if (data.badge !== undefined) payload.badge = data.badge;
      if (data.rating !== undefined) payload.rating = data.rating;
      if (data.reviews !== undefined) payload.reviews = data.reviews;
      if (data.price !== undefined) payload.price = data.price;
      if (data.originalPrice !== undefined) payload.original_price = data.originalPrice;
      if (data.stock !== undefined) payload.stock = data.stock;
      if (data.shortDesc !== undefined) payload.short_desc = data.shortDesc;
      if (data.longDesc !== undefined) payload.long_desc = data.longDesc;
      if (data.images !== undefined) payload.images = data.images;
      if (data.highlights !== undefined) payload.highlights = data.highlights;
      if (data.specs !== undefined) payload.specs = data.specs;
      if (data.inBox !== undefined) payload.in_box = data.inBox;
      if (data.relatedIds !== undefined) payload.related_ids = data.relatedIds;

      const { data: updatedRows, error: err } = await supabase
        .from('products')
        .update(payload)
        .eq('id', id)
        .select();
      if (err) {
        console.error('updateProduct Supabase error:', err.message, err.details, err.hint);
        throw err;
      }
      console.log('updateProduct success, rows affected:', updatedRows?.length, 'payload:', payload);
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
      return true;
    } catch (e) {
      console.error('updateProduct error:', e);
      return false;
    }
  }, []);

  const deleteProduct = useCallback(async (id: number): Promise<boolean> => {
    try {
      const { error: err } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
      if (err) throw err;
      setProducts(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (e) {
      console.error('deleteProduct error:', e);
      return false;
    }
  }, []);

  return { products, loading, error, addProduct, updateProduct, deleteProduct, refetch: fetchProducts };
}
